import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { insights, careers, faqs } from '../mock/mockData';
import { SectionLabel, CTA, FloatingActions } from './Home';
import { PageHero } from './Services';
import WorldMap from '../components/WorldMap';
import RealWorldMap from '../components/RealWorldMap';
import ServiceAreas from '../components/ServiceAreas';
import useSeo from '../hooks/useSeo';
import { api } from '../lib/api';
import { useRegion } from '../contexts/RegionContext';
import { useLang } from '../contexts/LanguageContext';
import CustomContactForm from '../components/CustomContactForm';
import CustomQuoteForm from '../components/CustomQuoteForm';

import logger from '../lib/logger';
export const InsightsPage = () => {
  useSeo({ title: 'Insights & Blog', description: 'Combined articles, case studies and engineering thought-leadership from Land Drafters experts.', path: '/insights' });
  return (
  <>
    <PageHero title="Latest" highlight="Insights" typewriter={['Industry News','Engineering Trends','Case Studies']} subtitle="Stories, technical deep-dives, and industry perspectives from our global engineering team." breadcrumb="Insights" />
    <section className="py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Featured */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <Link to={`/insights/${insights[0].slug}`} className="group block relative overflow-hidden rounded-lg h-[440px]">
            <img src={insights[0].image} alt={insights[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116] via-[#0e1116]/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
              <div className="inline-block bg-gray-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded">{insights[0].category}</div>
              <h2 className="text-2xl md:text-3xl font-black mt-3 group-hover:text-orange-400 transition-colors">{insights[0].title}</h2>
              <p className="text-gray-300 mt-3 text-sm line-clamp-2">{insights[0].excerpt}</p>
              <div className="flex items-center gap-3 mt-4 text-xs text-gray-400">
                <span>{insights[0].date}</span><span>·</span><span>{insights[0].readTime}</span>
              </div>
            </div>
          </Link>
          <div className="grid gap-5 content-start">
            {insights.slice(1, 4).map(p => (
              <Link key={p.slug} to={`/insights/${p.slug}`} className="group flex gap-5 p-4 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-green-200">
                <img src={p.image} alt={p.title} className="w-32 h-32 object-cover rounded shrink-0" />
                <div>
                  <div className="text-[10px] text-orange-500 font-bold uppercase tracking-wider">{p.category}</div>
                  <h3 className="font-bold text-[#0e1116] mt-2 group-hover:text-green-600 transition-colors line-clamp-2">{p.title}</h3>
                  <div className="flex items-center gap-2 mt-3 text-[11px] text-gray-500"><span>{p.date}</span><span>·</span><span>{p.readTime}</span></div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* All */}
        <SectionLabel>All Articles</SectionLabel>
        <h2 className="text-3xl font-black mb-8 text-[#0e1116]">Browse Our <span className="text-green-600">Knowledge Base</span></h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {insights.map(p => (
            <Link key={p.slug} to={`/insights/${p.slug}`} className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="h-52 overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /></div>
              <div className="p-6">
                <div className="inline-block bg-orange-50 text-orange-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">{p.category}</div>
                <h3 className="font-bold text-lg mt-3 text-[#0e1116] group-hover:text-green-600 transition-colors line-clamp-2">{p.title}</h3>
                <p className="text-sm text-gray-500 mt-2 line-clamp-2">{p.excerpt}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                  <span>{p.date}</span><span>{p.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CTA />
    <FloatingActions />
  </>
  );
};

export const InsightDetail = () => {
  const slug = window.location.pathname.split('/').pop();
  const p = insights.find(x => x.slug === slug) || insights[0];
  useSeo({ title: p.title, description: p.excerpt || p.summary || 'Engineering insights from Land Drafters.', path: `/insights/${p.slug}`, type: 'article', image: p.image });
  return (
    <>
      <section className="py-20 bg-[#0e1116] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/insights" className="inline-flex items-center gap-1 text-green-400 text-xs font-bold uppercase tracking-wider mb-5 hover:text-orange-400"><Icons.ArrowLeft className="w-3.5 h-3.5" /> All Insights</Link>
          <div className="inline-block bg-gray-700 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded mb-4">{p.category}</div>
          <h1 className="text-4xl md:text-5xl font-black leading-tight">{p.title}</h1>
          <div className="flex items-center gap-4 mt-5 text-xs text-gray-400"><span>By Land Drafters Editorial</span><span>·</span><span>{p.date}</span><span>·</span><span>{p.readTime}</span></div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <img src={p.image} alt="" className="w-full h-[440px] object-cover rounded-lg mb-8" />
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-xl text-gray-600 leading-relaxed">{p.excerpt}</p>
            <p className="mt-6">In today&apos;s rapidly evolving infrastructure landscape, advanced engineering practices and digital methodologies have become essential drivers of project success. As global markets demand faster delivery, higher quality, and more sustainable solutions, integrated CAD drafting, BIM, GIS, and digital twin technologies are reshaping how complex projects are conceived, designed, and delivered.</p>
            <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">A New Era of Digital Engineering</h2>
            <p>From scan-to-BIM workflows that capture as-built conditions with millimeter accuracy, to digital twins that mirror the operational lifecycle of physical assets, the discipline of engineering has expanded far beyond traditional 2D drawings. Modern engineering teams now operate across an interconnected ecosystem of data, models, and analytics.</p>
            <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">Practical Outcomes for Clients</h2>
            <p>The result for our clients is tangible: reduced rework, faster permit cycles, more accurate construction estimates, and infrastructure assets that perform reliably across decades of operation. Land Drafters&apos; integrated engineering approach combines deep technical expertise with cost-efficient global delivery, ensuring every project receives the attention to detail it deserves.</p>
            <blockquote className="border-l-4 border-gray-600 pl-5 my-8 italic text-gray-600">
              “The future of engineering lies not in any single tool, but in the seamless integration of multiple disciplines, technologies, and human expertise.”
            </blockquote>
            <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">Looking Ahead</h2>
            <p>As we look to the next decade, the firms that thrive will be those that invest in both technology and people. Land Drafters continues to expand its capabilities, training its engineers in the latest platforms while maintaining the deep expertise that has earned us the trust of over 350 active client partners.</p>
          </div>
          <div className="mt-12 p-8 bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg text-white">
            <h3 className="text-2xl font-black">Ready to start your next project?</h3>
            <p className="text-green-100 mt-2">Connect with our engineering experts and get a quote in 24 hours.</p>
            <Link to="/get-quote" className="inline-flex items-center gap-2 mt-5 bg-white text-green-700 hover:bg-orange-500 hover:text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider transition-colors">Get a Quote <Icons.ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
      <FloatingActions />
    </>
  );
};

export const CareersPage = () => {
  useSeo({ title: 'Careers', description: 'Join 350+ engineers building the future of global engineering and CAD drafting at Land Drafters. Explore open roles.', path: '/careers' });
  const [filter, setFilter] = useState('All');
  const [applyFor, setApplyFor] = useState(null);
  const [applied, setApplied] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ld_applied') || '[]'); } catch { return []; }
  });
  const depts = ['All', ...new Set(careers.map(c => c.dept))];
  const filtered = filter === 'All' ? careers : careers.filter(c => c.dept === filter);

  const onApplied = (title) => {
    const next = Array.from(new Set([...applied, title]));
    setApplied(next);
    try { localStorage.setItem('ld_applied', JSON.stringify(next)); }
    catch (e) { logger.warn('[Careers] applied list persist failed:', e); }
  };

  return (
    <>
      <PageHero title="Careers at" highlight="Land Drafters" typewriter={['Empowering You','Grow With Us','Build Together']} subtitle="Take your first step towards the career of your dreams. Be part of a team that is global in ambition, local in manner and style." breadcrumb="Careers" />
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {[{ icon: 'Rocket', title: 'Grow Your Career', d: 'Continuous learning programs, technical training, and certifications across leading CAD/BIM/GIS platforms.' },
              { icon: 'Users', title: 'Collaborative Culture', d: 'Work alongside 350+ multi-disciplinary engineers in an environment that celebrates creativity and excellence.' },
              { icon: 'Globe', title: 'Global Exposure', d: 'Work on projects across 5 continents — from US infrastructure to global energy and industrial projects.' }].map((b) => {
              const Ic = Icons[b.icon]; return (
                <div key={b.title} className="bg-white rounded-lg p-7 border border-gray-200">
                  <div className="w-12 h-12 rounded bg-orange-100 text-orange-600 flex items-center justify-center mb-4"><Ic className="w-6 h-6" /></div>
                  <h3 className="font-bold text-lg text-[#0e1116]">{b.title}</h3>
                  <p className="text-sm text-gray-500 mt-2">{b.d}</p>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h2 className="text-3xl font-black text-[#0e1116]">Open Positions</h2>
            <div className="flex flex-wrap gap-2">
              {depts.map(d => <button key={d} onClick={() => setFilter(d)} className={`px-4 py-2 rounded text-xs font-bold uppercase tracking-wider transition-colors ${filter === d ? 'bg-gray-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-green-400'}`}>{d}</button>)}
            </div>
          </div>

          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c.title + c.location} className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:border-green-400 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded bg-gray-100 text-green-700 flex items-center justify-center"><Icons.Briefcase className="w-6 h-6" /></div>
                  <div>
                    <h3 className="font-bold text-lg text-[#0e1116]">{c.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Icons.MapPin className="w-3 h-3" /> {c.location}</span>
                      <span className="flex items-center gap-1"><Icons.Clock className="w-3 h-3" /> {c.type}</span>
                      <span className="flex items-center gap-1"><Icons.Tag className="w-3 h-3" /> {c.dept}</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => { if (!applied.includes(c.title)) setApplyFor(c); }} className={`inline-flex items-center gap-2 ${applied.includes(c.title) ? 'bg-gray-100 text-green-700 cursor-default' : 'bg-[#0e1116] hover:bg-gray-700 text-white'} font-bold px-5 py-3 rounded uppercase text-xs tracking-wider transition-colors`}>
                  {applied.includes(c.title) ? <><Icons.Check className="w-4 h-4" /> Applied</> : <>Apply Now <Icons.ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {applyFor && (
        <ApplyModal job={applyFor} onClose={() => setApplyFor(null)} onSuccess={() => { onApplied(applyFor.title); setApplyFor(null); }} />
      )}

      <FloatingActions />
    </>
  );
};

const ApplyModal = ({ job, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    const f = new FormData(e.target);
    try {
      await api.careerApply({
        name: f.get('name') || '',
        email: f.get('email') || '',
        phone: f.get('phone') || '',
        job_title: job.title,
        location: job.location,
        department: job.dept,
        cover_letter: f.get('cover') || ''
      });
      onSuccess();
    } catch (er) {
      setErr(er.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-green-600 font-bold">Apply for</div>
            <h3 className="font-black text-lg text-[#0e1116]">{job.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{job.dept} · {job.location}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-orange-600" aria-label="close"><Icons.X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <LightInput label="Full Name *" name="name" required />
          <LightInput label="Email *" type="email" name="email" required />
          <LightInput label="Phone" type="tel" name="phone" />
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600 font-bold">Cover Letter</label>
            <textarea name="cover" rows={4} className="mt-2 w-full bg-white border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" placeholder="Tell us why you'd be a great fit..."></textarea>
          </div>
          {err && <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2"><Icons.AlertCircle className="w-4 h-4" /> {err}</div>}
          <button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2">
            {loading ? <><Icons.Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Submit Application <Icons.Send className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export const ContactPage = () => {
  useSeo({ title: 'Contact Us', description: 'Get in touch with Land Drafters — 24/7 support, global offices and dedicated engineering teams for every project.', path: '/contact' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const { region } = useRegion();
  const { lang } = useLang();
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrMsg('');
    const f = new FormData(e.target);
    try {
      await api.contact({
        first_name: f.get('firstName') || '',
        last_name: f.get('lastName') || '',
        email: f.get('email') || '',
        phone: f.get('phone') || '',
        company: f.get('company') || '',
        website: f.get('website') || '',
        requirement: f.get('req') || '',
        region: region?.code || 'global',
        language: lang
      });
      setSubmitted(true);
      e.target.reset();
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <PageHero title="Get In" highlight="Touch" typewriter={['Talk to Experts','Start a Project','Say Hello']} subtitle="From feasibility to final delivery — tell us about your project and let our global engineering team develop the solution." breadcrumb="Contact Us" />
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            {[
              { icon: 'MapPin', title: `${region?.name || 'Global'} Office`, desc: region?.address || 'Apex, North Carolina, USA' },
              { icon: 'Phone', title: 'Call Us', desc: region?.phone || '(+1) 786-673-2523', link: `tel:${(region?.phone || '+17866732523').replace(/\s/g, '')}`, desc2: '+91 9960 250 279', link2: 'tel:+919960250279' },
              { icon: 'Mail', title: 'Email Us', desc: region?.email || 'info@landdrafters.com', link: `mailto:${region?.email || 'info@landdrafters.com'}`, desc2: 'landdrafters@gmail.com', link2: 'mailto:landdrafters@gmail.com' },
              { icon: 'Banknote', title: 'Currency', desc: `${region?.currency || 'USD'} · ${region?.timezone || 'EST'}` },
              { icon: 'Clock', title: 'Support Hours', desc: '24/7 Customer Support' }
            ].map((c) => {
              const Ic = Icons[c.icon];
              return (
                <div key={c.title} className="flex gap-4 p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 rounded bg-gray-600 text-white flex items-center justify-center shrink-0"><Ic className="w-5 h-5" /></div>
                  <div>
                    <h4 className="font-bold text-[#0e1116]">{c.title}</h4>
                    {c.link ? <a href={c.link} className="block text-sm text-gray-600 hover:text-green-600">{c.desc}</a> : <p className="text-sm text-gray-600">{c.desc}</p>}
                    {c.desc2 && c.desc2 !== c.desc && <a href={c.link2} className="block text-sm text-gray-600 hover:text-green-600">{c.desc2}</a>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            <CustomContactForm />
          </div>
        </div>
      </section>
      <RealWorldMap compact />
      <FloatingActions />
    </>
  );
};

const Input = ({ label, ...p }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-green-400 font-bold">{label}</label>
    <input {...p} className="mt-2 w-full bg-[#1a1f2e] border border-gray-900/50 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" />
  </div>
);

export const GetQuotePage = () => {
  useSeo({ title: 'Get a Quote', description: 'Request a fast, no-obligation engineering or CAD drafting quote from Land Drafters — turnaround in 24-48 hours.', path: '/get-quote' });
  return (
    <>
      <PageHero title="Get a" highlight="Free Quote" typewriter={['24hr Response','Transparent Pricing','No Obligation']} subtitle="Tell us about your project. We listen, scope, and confirm — with detailed timeline and pricing within 24 hours." breadcrumb="Get Quote" />
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <CustomQuoteForm />
        </div>
      </section>
      <RealWorldMap compact />
      <FloatingActions />
    </>
  );
};

const LightInput = ({ label, ...p }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-gray-600 font-bold">{label}</label>
    <input {...p} className="mt-2 w-full bg-white border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" />
  </div>
);

export const PrivacyPage = () => {
  useSeo({ title: 'Privacy Policy', description: 'How Land Drafters collects, uses and protects your information across our global operations.', path: '/privacy-policy' });
  return (
  <>
    <PageHero title="Privacy" highlight="Policy" subtitle="How Land Drafters collects, uses, and protects your information across all our services." breadcrumb="Legal" />
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-6 prose prose-lg text-gray-700">
        <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
        <h2 className="text-2xl font-black text-[#0e1116]">1. Introduction</h2>
        <p>Land Drafters LLC (“we”, “our”, or “us”) respects your privacy and is committed to protecting it through our compliance with this policy. This policy describes how we collect, use, disclose, and protect personal information that we collect from visitors and clients across our global engineering and CAD drafting services.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">2. Information We Collect</h2>
        <p>We collect information you provide directly to us when you request a quote, contact us, subscribe to insights, or submit a job application. This includes name, email, phone number, company details, project files, and any additional information you choose to share.</p>
        <p>We also automatically collect certain information when you visit our website, including IP address, browser type, pages viewed, and approximate location data via cookies and similar technologies.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">3. How We Use Your Information</h2>
        <ul>
          <li>To provide and improve our engineering, CAD drafting, BIM, GIS, and consulting services</li>
          <li>To respond to inquiries, quote requests, and project communications</li>
          <li>To send relevant industry insights and updates (only with consent)</li>
          <li>To process job applications and recruitment</li>
          <li>To meet legal, regulatory, and contractual obligations</li>
        </ul>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">4. Data Security</h2>
        <p>We are an ISO 9001:2015 certified organization with enterprise-grade data security protocols. All client project data is treated with strict confidentiality and protected through encryption, NDAs, and role-based access controls.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">5. Sharing of Information</h2>
        <p>We do not sell your personal information. We may share information only with: (a) authorised internal teams across our US and India operations; (b) verified third-party service providers acting on our behalf under confidentiality; (c) legal authorities when required by law.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">6. Cookies & Tracking</h2>
        <p>Our website uses cookies to improve user experience, analyse traffic, and remember preferences. You can disable cookies through your browser settings, though some functionality may be reduced.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">7. Your Rights</h2>
        <p>Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict processing of your personal information. To exercise these rights, contact us at info@landdrafters.com.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">8. International Data Transfers</h2>
        <p>As a global organization, your information may be transferred to and processed in countries other than your own, including the United States and India. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">9. Children&apos;s Privacy</h2>
        <p>Our services are intended for business and professional use. We do not knowingly collect information from children under 16.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">10. Changes to This Policy</h2>
        <p>We may update this policy from time to time. The latest version will always be posted on this page with the updated date.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">11. Contact Us</h2>
        <p>For any privacy-related inquiries, please contact: <a href="mailto:info@landdrafters.com" className="text-green-600 font-bold">info@landdrafters.com</a></p>
      </div>
    </section>
    <FloatingActions />
  </>
  );
};

export const TermsPage = () => {
  useSeo({ title: 'Terms & Conditions', description: 'Terms governing the use of Land Drafters services, deliverables and digital properties.', path: '/terms' });
  return (
  <>
    <PageHero title="Terms &" highlight="Conditions" subtitle="The terms governing your use of Land Drafters services, website, and engagements." breadcrumb="Legal" />
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-6 prose prose-lg text-gray-700">
        <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
        <h2 className="text-2xl font-black text-[#0e1116]">1. Acceptance of Terms</h2>
        <p>By accessing or using the Land Drafters LLC website (“the Site”) and any of our engineering, CAD drafting, BIM, GIS, or consulting services (“the Services”), you agree to comply with these Terms & Conditions. If you do not agree, please do not use our Site or Services.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">2. Services Description</h2>
        <p>Land Drafters provides multi-disciplinary engineering and CAD drafting services including but not limited to land surveying, BIM modeling, civil engineering, structural engineering, architectural drafting, MEP engineering, mechanical engineering, manufacturing engineering, plant engineering, environmental engineering, GIS, and digital transformation services.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">3. Quotations & Engagements</h2>
        <p>All quotations issued by Land Drafters are valid for 30 days unless otherwise specified. Engagements commence upon written acceptance of the quote or signed Statement of Work (SOW).</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">4. Intellectual Property</h2>
        <p>All deliverables produced under a signed engagement become the property of the client upon full payment. Land Drafters retains the right to reference work in portfolios and marketing materials in a non-confidential manner, unless specifically restricted by NDA.</p>
        <p>The content, designs, and materials on this Site are owned by Land Drafters LLC and protected by applicable copyright and trademark laws. You may not reproduce, distribute, or use them without prior written consent.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">5. Payment Terms</h2>
        <p>Unless otherwise agreed, invoices are due within 30 days of issuance. Late payments may incur interest charges and result in suspension of services. All fees are quoted exclusive of applicable taxes.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">6. Quality & Revisions</h2>
        <p>We are committed to delivering high-quality work meeting industry standards. Standard engagements include unlimited revisions until client approval, scoped to the original requirements. Out-of-scope work may require an updated quote.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">7. Confidentiality</h2>
        <p>Land Drafters treats all client project information as strictly confidential. We are happy to sign Non-Disclosure Agreements prior to commencement of any engagement.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">8. Limitation of Liability</h2>
        <p>Land Drafters&apos; liability for any claim arising from a project is limited to the total fees paid by the client for that specific engagement. We are not liable for indirect, incidental, or consequential damages.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">9. Indemnification</h2>
        <p>Clients agree to indemnify Land Drafters from any third-party claims arising from client-provided data, intentional misuse of deliverables, or unauthorized representation of engineering certifications.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">10. Termination</h2>
        <p>Either party may terminate an engagement with 30 days written notice. Client remains liable for fees on all work completed up to the date of termination.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">11. Governing Law</h2>
        <p>These Terms are governed by the laws of the State of North Carolina, United States, without regard to its conflict-of-law principles. Any disputes shall be resolved in the courts of Wake County, North Carolina.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">12. Changes to Terms</h2>
        <p>Land Drafters may update these Terms at any time. Continued use of the Site or Services after changes constitutes acceptance of the revised Terms.</p>

        <h2 className="text-2xl font-black text-[#0e1116] mt-8">13. Contact</h2>
        <p>For any questions about these Terms, please contact: <a href="mailto:info@landdrafters.com" className="text-green-600 font-bold">info@landdrafters.com</a></p>
      </div>
    </section>
    <FloatingActions />
  </>
  );
};

export const CoveragePage = () => {
  useSeo({ title: 'Global Coverage', description: 'Land Drafters operates across 40+ countries with delivery centers in the US and India. Explore our worldwide footprint.', path: '/coverage' });
  return (
    <>
      <PageHero title="Global" highlight="Coverage" typewriter={['Americas', 'Europe', 'Asia-Pacific', 'Middle East', 'Oceania']} subtitle="Land Drafters serves projects across 40+ countries on five continents. Explore our regional presence and discover engineering services tailored to your local market." breadcrumb="Worldwide" />
      <RealWorldMap />
      <ServiceAreas />
      <CTA />
      <FloatingActions />
    </>
  );
};
